import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, CardContent, CardHeader, CardTitle, Checkbox, Input } from '@mlm/ui';
import {
  createReplicatedPage,
  deleteReplicatedPage,
  getTenantFeatures,
  listProducts,
  listReplicatedPages,
  patchTenantFeatures,
  replicatedImageUrl,
  updateReplicatedPage,
  uploadReplicatedImage,
  type ReplicatedPage,
} from '../lib/api';
import { RpPageEditor } from '../components/replicated/page-editor';

// `ReplicatedPage` types every editable column (ctaLabel, timer*, seo*).
type EditablePage = ReplicatedPage;

const DEFAULT_CTA_LABEL = 'Sign up';
// Matches the API's `ctaLabelSchema` cap, so the field can't submit a rejected value.
const CTA_LABEL_MAX = 40;
const DEFAULT_TIMER_LABEL = 'Offer ends in';
// Matches the API's `timerHoursSchema` bounds.
const TIMER_HOURS_MAX = 720;

/**
 * Replicated-link landing pages. This file owns the PAGE-level surface — the
 * master switch, the page list (slug / CTA / timer / default / enabled) and
 * campaign package tagging. The page's CONTENT is built by `RpPageEditor`
 * (components/replicated/), the visual section builder.
 *
 * Typed editors only — no raw JSON.
 */
export function ReplicatedPagePage() {
  const qc = useQueryClient();
  const pagesQuery = useQuery({ queryKey: ['admin-replicated-pages'], queryFn: listReplicatedPages });
  const featuresQuery = useQuery({ queryKey: ['tenant-features'], queryFn: getTenantFeatures });

  const pages: EditablePage[] = pagesQuery.data?.pages ?? [];
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // Select the default page (or first) once loaded / after the selection is gone.
  useEffect(() => {
    if (selectedId && pages.some((p) => p.id === selectedId)) return;
    const def = pages.find((p) => p.isDefault) ?? pages[0];
    setSelectedId(def?.id ?? null);
  }, [pages, selectedId]);
  const selectedPage = pages.find((p) => p.id === selectedId) ?? null;

  const enabled = featuresQuery.data?.replicated_page_enabled ?? false;

  const toggle = useMutation({
    meta: { successMessage: 'Setting saved.' },
    mutationFn: (next: boolean) => patchTenantFeatures({ replicated_page_enabled: next }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tenant-features'] }),
  });

  const invalidatePages = () => qc.invalidateQueries({ queryKey: ['admin-replicated-pages'] });

  const [newTitle, setNewTitle] = useState('');
  const [newSlug, setNewSlug] = useState('');
  const createPage = useMutation({
    meta: { successMessage: 'Page created.' },
    mutationFn: (input: { slug: string; title: string }) => createReplicatedPage(input),
    onSuccess: (res) => {
      setNewTitle('');
      setNewSlug('');
      setSelectedId(res.page.id);
      invalidatePages();
    },
  });

  const updatePage = useMutation({
    meta: { successMessage: 'Page updated.' },
    mutationFn: ({ id, patch }: { id: string; patch: Partial<EditablePage> }) =>
      updateReplicatedPage(id, patch),
    onSuccess: () => invalidatePages(),
  });

  const removePage = useMutation({
    meta: { successMessage: 'Page deleted.' },
    mutationFn: (id: string) => deleteReplicatedPage(id),
    onSuccess: (_res, id) => {
      if (selectedId === id) setSelectedId(null);
      invalidatePages();
    },
  });

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold">Replicated Link Pages</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Build the landing pages a prospect sees when they open a member's referral link. Each page
          has its own slug, shared as <span className="font-mono">/r/&lt;code&gt;/&lt;slug&gt;</span>; the
          default page shows at the bare <span className="font-mono">/r/&lt;code&gt;</span>. The sponsor's
          referral code is always carried through to signup.
        </p>
      </div>

      <Card>
        <CardContent className="pt-6">
          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={enabled}
              onCheckedChange={(v) => toggle.mutate(v === true)}
              disabled={toggle.isPending}
            />
            Enable custom replicated landing pages (master switch)
          </label>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Pages</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {pages.length === 0 ? (
            <p className="text-sm text-muted-foreground">No pages yet. Create one below.</p>
          ) : (
            <ul className="space-y-2">
              {pages.map((p) => (
                <PageRow
                  key={p.id}
                  page={p}
                  selected={p.id === selectedId}
                  busy={updatePage.isPending || removePage.isPending}
                  onSelect={() => setSelectedId(p.id)}
                  onRename={(title) => title !== p.title && updatePage.mutate({ id: p.id, patch: { title } })}
                  onSlug={(slug) => slug !== p.slug && updatePage.mutate({ id: p.id, patch: { slug } })}
                  onCtaLabel={(ctaLabel) =>
                    ctaLabel !== (p.ctaLabel ?? DEFAULT_CTA_LABEL) &&
                    updatePage.mutate({ id: p.id, patch: { ctaLabel } })
                  }
                  onCtaAuto={(ctaAuto) => updatePage.mutate({ id: p.id, patch: { ctaAuto } })}
                  onSeo={(patch) => updatePage.mutate({ id: p.id, patch })}
                  onTimer={(patch) => updatePage.mutate({ id: p.id, patch })}
                  onToggleEnabled={(en) => updatePage.mutate({ id: p.id, patch: { enabled: en } })}
                  onMakeDefault={() => updatePage.mutate({ id: p.id, patch: { isDefault: true } })}
                  onDelete={() => {
                    if (window.confirm(`Delete page "${p.title}"? Its blocks are removed.`))
                      removePage.mutate(p.id);
                  }}
                />
              ))}
            </ul>
          )}
          <div className="flex flex-wrap items-end gap-2 border-t pt-3">
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">New page title</label>
              <Input
                className="w-48"
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                placeholder="e.g. Product Launch"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Slug (optional)</label>
              <Input
                className="w-40 font-mono"
                value={newSlug}
                onChange={(e) => setNewSlug(e.target.value)}
                placeholder="product-launch"
              />
            </div>
            <Button
              onClick={() =>
                createPage.mutate({ title: newTitle.trim(), slug: newSlug.trim() || newTitle.trim() })
              }
              disabled={createPage.isPending || !newTitle.trim()}
            >
              {createPage.isPending ? 'Adding…' : '+ Add page'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {!selectedPage ? (
        <Card>
          <CardContent className="pt-6 text-sm text-muted-foreground">
            Select or create a page to edit its content.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          <RpPageEditor
            key={selectedPage.id}
            page={selectedPage}
            pages={pages}
            // mutateAsync (not mutate) so the editor can sequence this INSIDE
            // its save rather than firing it off independently.
            onPagePatch={(patch) => updatePage.mutateAsync({ id: selectedPage.id, patch })}
          />
          <CampaignPackagesCard page={selectedPage} />
        </div>
      )}
    </div>
  );
}

/**
 * Package tagging for ONE campaign page. Ticking activation kits scopes leads
 * who sign up through this campaign's link (`/r/<ref>/<slug>`) to just those
 * kits — INTERSECTED with the sponsor tier's own package list, so a campaign
 * can only ever narrow what a sponsor is already allowed to sell. Leave every
 * kit unticked for no campaign-level narrowing (tier scoping still applies).
 */
function CampaignPackagesCard({ page }: { page: ReplicatedPage }) {
  const qc = useQueryClient();
  const kitsQuery = useQuery({
    queryKey: ['admin-products', 'kits'],
    queryFn: () => listProducts({ activeOnly: true, pageSize: 200 }),
  });
  const kits = (kitsQuery.data ?? []).filter((p) => p.isKit);
  const [selected, setSelected] = useState<Set<string>>(() => new Set(page.kitProductIds ?? []));
  const [dirty, setDirty] = useState(false);

  // Re-sync when the admin switches to a different page.
  useEffect(() => {
    setSelected(new Set(page.kitProductIds ?? []));
    setDirty(false);
  }, [page.id, page.kitProductIds]);

  const save = useMutation({
    mutationFn: () => updateReplicatedPage(page.id, { kitProductIds: Array.from(selected) }),
    meta: { successMessage: `Packages saved for "${page.title}".` },
    onSuccess: () => {
      setDirty(false);
      qc.invalidateQueries({ queryKey: ['admin-replicated-pages'] });
    },
  });

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base">
          Packages — <span className="text-muted-foreground">{page.title}</span>
        </CardTitle>
        <Button size="sm" disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? 'Saving…' : 'Save packages'}
        </Button>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-muted-foreground">
          Tick the activation kits a lead may buy when they arrive through this campaign's link.
          This <span className="font-medium">narrows</span> the sponsor tier's own package list (a
          campaign can never unlock a kit the sponsor's tier already hides). Leave everything
          unticked for no campaign-level restriction.
        </p>
        {kits.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No activation kits yet — add kit products first, then tag them here.
          </p>
        ) : (
          <div className="flex flex-wrap gap-x-4 gap-y-1.5">
            {kits.map((k) => (
              <label key={k.id} className="flex items-center gap-1.5 text-xs">
                <input
                  type="checkbox"
                  className="h-3.5 w-3.5"
                  checked={selected.has(k.id)}
                  onChange={(e) => {
                    setSelected((prev) => {
                      const next = new Set(prev);
                      if (e.target.checked) next.add(k.id);
                      else next.delete(k.id);
                      return next;
                    });
                    setDirty(true);
                  }}
                />
                {k.name}
              </label>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/** One row in the Pages list: select-to-edit, rename, slug, CTA, default, enable, delete. */
function PageRow({
  page,
  selected,
  busy,
  onSelect,
  onRename,
  onSlug,
  onCtaLabel,
  onCtaAuto,
  onSeo,
  onTimer,
  onToggleEnabled,
  onMakeDefault,
  onDelete,
}: {
  page: EditablePage;
  selected: boolean;
  busy: boolean;
  onSelect: () => void;
  onRename: (title: string) => void;
  onSlug: (slug: string) => void;
  onCtaLabel: (ctaLabel: string) => void;
  onCtaAuto: (ctaAuto: boolean) => void;
  onSeo: (patch: { seoTitle?: string | null; seoDescription?: string | null; ogImageKey?: string | null }) => void;
  onTimer: (patch: { timerEnabled?: boolean; timerHours?: number; timerLabel?: string }) => void;
  onToggleEnabled: (enabled: boolean) => void;
  onMakeDefault: () => void;
  onDelete: () => void;
}) {
  const [title, setTitle] = useState(page.title);
  const [slug, setSlug] = useState(page.slug);
  const [ctaLabel, setCtaLabel] = useState(page.ctaLabel ?? DEFAULT_CTA_LABEL);
  const [timerHours, setTimerHours] = useState(String(page.timerHours ?? 24));
  const [timerLabel, setTimerLabel] = useState(page.timerLabel ?? DEFAULT_TIMER_LABEL);
  useEffect(() => setTitle(page.title), [page.title]);
  useEffect(() => setSlug(page.slug), [page.slug]);
  useEffect(() => setCtaLabel(page.ctaLabel ?? DEFAULT_CTA_LABEL), [page.ctaLabel]);
  useEffect(() => setTimerHours(String(page.timerHours ?? 24)), [page.timerHours]);
  useEffect(() => setTimerLabel(page.timerLabel ?? DEFAULT_TIMER_LABEL), [page.timerLabel]);
  return (
    <li
      className={`flex flex-wrap items-center gap-2 rounded-md border p-2 ${selected ? 'border-primary bg-primary/5' : ''}`}
    >
      <Button size="sm" variant={selected ? 'default' : 'outline'} onClick={onSelect}>
        {selected ? 'Editing' : 'Edit'}
      </Button>
      <Input
        className="h-8 w-44"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        onBlur={() => onRename(title.trim() || page.title)}
      />
      <div className="flex items-center gap-1">
        <span className="text-[11px] text-muted-foreground">/r/…/</span>
        <Input
          className="h-8 w-36 font-mono"
          value={slug}
          onChange={(e) => setSlug(e.target.value)}
          onBlur={() => onSlug(slug.trim() || page.slug)}
        />
      </div>
      <div className="flex items-center gap-1">
        <label
          className="flex items-center gap-1 text-[11px] text-muted-foreground"
          title="Adds a sign-up button below the page content. Turn it off once the page has its own call-to-action section, or visitors see two stacked buttons."
        >
          <Checkbox
            checked={page.ctaAuto !== false}
            onCheckedChange={(v) => onCtaAuto(v === true)}
            disabled={busy}
          />
          Auto CTA
        </label>
        {page.ctaAuto !== false ? (
          <Input
            className="h-8 w-32"
            value={ctaLabel}
            maxLength={CTA_LABEL_MAX}
            onChange={(e) => setCtaLabel(e.target.value)}
            // Blank is rejected server-side (an empty label = an invisible
            // button), so an emptied field snaps back to the saved wording.
            onBlur={() => {
              const next = ctaLabel.trim() || page.ctaLabel || DEFAULT_CTA_LABEL;
              setCtaLabel(next);
              onCtaLabel(next);
            }}
            placeholder={DEFAULT_CTA_LABEL}
            title="Label of the sign-up button added below this page's content"
          />
        ) : null}
      </div>
      <div className="flex items-center gap-1">
        <label
          className="flex items-center gap-1 text-[11px] text-muted-foreground"
          title="Rolling countdown banner. Each visitor's timer starts at the full duration on their first visit, counts to 0, then restarts."
        >
          <Checkbox
            checked={page.timerEnabled ?? false}
            onCheckedChange={(v) => onTimer({ timerEnabled: v === true })}
            disabled={busy}
          />
          Timer
        </label>
        {(page.timerEnabled ?? false) && (
          <>
            <Input
              className="h-8 w-16"
              type="number"
              min={1}
              max={TIMER_HOURS_MAX}
              value={timerHours}
              onChange={(e) => setTimerHours(e.target.value)}
              onBlur={() => {
                const n = Math.min(TIMER_HOURS_MAX, Math.max(1, Math.floor(Number(timerHours)) || 24));
                setTimerHours(String(n));
                if (n !== page.timerHours) onTimer({ timerHours: n });
              }}
              title="Countdown duration in hours (default 24)"
            />
            <span className="text-[11px] text-muted-foreground">h</span>
            <Input
              className="h-8 w-32"
              value={timerLabel}
              maxLength={80}
              onChange={(e) => setTimerLabel(e.target.value)}
              onBlur={() => {
                const next = timerLabel.trim() || page.timerLabel || DEFAULT_TIMER_LABEL;
                setTimerLabel(next);
                if (next !== page.timerLabel) onTimer({ timerLabel: next });
              }}
              placeholder={DEFAULT_TIMER_LABEL}
              title="Wording shown beside the countdown"
            />
          </>
        )}
      </div>
      {page.isDefault ? (
        <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase text-muted-foreground">
          Default
        </span>
      ) : (
        <Button size="sm" variant="ghost" disabled={busy} onClick={onMakeDefault}>
          Set default
        </Button>
      )}
      <label className="ml-auto flex items-center gap-1 text-xs">
        <Checkbox
          checked={page.enabled}
          onCheckedChange={(v) => onToggleEnabled(v === true)}
          disabled={busy}
        />
        Enabled
      </label>
      <Button size="sm" variant="ghost" disabled={busy} onClick={onDelete} title="Delete page">
        ✕
      </Button>

      {selected ? <SharePreviewFields page={page} onChange={onSeo} /> : null}
    </li>
  );
}

/**
 * What this page looks like when its link is pasted into Facebook, Messenger or
 * Viber — which, for a referral link, is most of how it travels.
 *
 * All three fall back to something sensible when blank (the page title, its first
 * paragraph, its hero image), so filling them in is an override rather than a
 * chore. They are consumed by the crawler prerender, not by the page itself.
 */
function SharePreviewFields({
  page,
  onChange,
}: {
  page: EditablePage;
  onChange: (patch: { seoTitle?: string | null; seoDescription?: string | null; ogImageKey?: string | null }) => void;
}) {
  const [title, setTitle] = useState(page.seoTitle ?? '');
  const [desc, setDesc] = useState(page.seoDescription ?? '');
  useEffect(() => setTitle(page.seoTitle ?? ''), [page.seoTitle]);
  useEffect(() => setDesc(page.seoDescription ?? ''), [page.seoDescription]);

  return (
    <details className="w-full basis-full rounded-md border bg-background/50">
      <summary className="cursor-pointer select-none px-2 py-1.5 text-[11px] font-medium text-muted-foreground">
        Share preview (Facebook / Messenger)
      </summary>
      <div className="flex flex-wrap items-end gap-2 border-t p-2">
        <div className="space-y-1">
          <label className="block text-[11px] text-muted-foreground">Title</label>
          <Input
            className="h-8 w-56"
            value={title}
            maxLength={200}
            placeholder={page.title}
            onChange={(e) => setTitle(e.target.value)}
            onBlur={() => {
              const next = title.trim();
              if (next !== (page.seoTitle ?? '')) onChange({ seoTitle: next || null });
            }}
          />
        </div>
        <div className="space-y-1">
          <label className="block text-[11px] text-muted-foreground">Description</label>
          <Input
            className="h-8 w-80"
            value={desc}
            maxLength={400}
            placeholder="Falls back to the page's opening copy"
            onChange={(e) => setDesc(e.target.value)}
            onBlur={() => {
              const next = desc.trim();
              if (next !== (page.seoDescription ?? '')) onChange({ seoDescription: next || null });
            }}
          />
        </div>
        <div className="space-y-1">
          <label className="block text-[11px] text-muted-foreground">Image</label>
          <ShareImageField
            value={page.ogImageKey ?? ''}
            onChange={(key) => onChange({ ogImageKey: key || null })}
          />
        </div>
      </div>
    </details>
  );
}

function ShareImageField({ value, onChange }: { value: string; onChange: (key: string) => void }) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  return (
    <div className="flex items-center gap-2">
      {value ? (
        <img src={replicatedImageUrl(value)} alt="" className="h-8 w-14 rounded border object-cover" />
      ) : (
        <div className="h-8 w-14 rounded border bg-muted" />
      )}
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={async (e) => {
          const f = e.target.files?.[0];
          if (!f) return;
          setUploading(true);
          try {
            const { key } = await uploadReplicatedImage(f);
            onChange(key);
          } finally {
            setUploading(false);
          }
        }}
      />
      <Button
        type="button"
        size="sm"
        variant="outline"
        disabled={uploading}
        onClick={() => fileRef.current?.click()}
      >
        {uploading ? 'Uploading…' : value ? 'Replace' : 'Upload'}
      </Button>
      {value ? (
        <button
          type="button"
          className="text-[11px] text-muted-foreground hover:text-destructive"
          onClick={() => onChange('')}
        >
          Clear
        </button>
      ) : null}
    </div>
  );
}
